import { copyText } from "@/lib/uid.ts";
import type { LoginState } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, TerminalSquare } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { accountsApi } from "@/api/accounts.ts";
import { Alert, Button, Input, Spinner } from "@/ui/primitives.tsx";
import { Terminal } from "@/ui/Terminal.tsx";
import { useApiError } from "@/ui/useApiError.ts";

/**
 * Guided provider login: the CLI runs in a hidden PTY on the server. We open the link it prints in a new
 * tab, the user authorizes and pastes the code here. The raw terminal remains available as a fallback.
 */
export function LoginPanel({ accountId, onDone }: { accountId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const state = useQuery({ queryKey: ["accounts", accountId, "login"], queryFn: () => accountsApi.loginState(accountId), refetchInterval: (q) => (q.state.data?.phase === "starting" || q.state.data?.phase === "finishing" || (q.state.data?.phase === "waiting_code" && q.state.data.mode === "device") ? 1000 : false) });
  useEffect(() => { if (state.data?.phase === "done") { void qc.invalidateQueries({ queryKey: ["accounts"] }); onDone(); } }, [state.data?.phase]);
  const setState = (s: LoginState) => qc.setQueryData(["accounts", accountId, "login"], s);
  const start = useMutation({
    mutationFn: async () => {
      const w = window.open("about:blank", "_blank"); // opened synchronously so popup blockers allow it
      try {
        const s = await accountsApi.loginStart(accountId);
        if (s.url && w) w.location.href = s.url;
        else w?.close();
        return s;
      } catch (e) {
        w?.close();
        throw e;
      }
    },
    onSuccess: setState,
  });
  const submit = useMutation({
    mutationFn: () => accountsApi.loginCode(accountId, code),
    onSuccess: (s) => { setState(s); setCode(""); if (s.phase === "done") { void qc.invalidateQueries({ queryKey: ["accounts"] }); onDone(); } },
  });
  const s = state.data ?? null;
  const device = s?.mode === "device";
  const copy = () => { if (s?.url) { void copyText(s.url); setCopied(true); setTimeout(() => setCopied(false), 1500); } };

  if (advanced) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-fg-muted">{t("accounts.loginHint")}</p>
        <Terminal target={`account:${accountId}`} onExit={onDone} />
        <Button size="sm" variant="ghost" onClick={() => setAdvanced(false)}>{t("common.back")}</Button>
      </div>
    );
  }
  return (
    <div className="space-y-3 rounded-lg border border-border bg-surface-2/40 p-4">
      <ol className="list-decimal space-y-1 ps-5 text-sm">
        <li>{t("accounts.guided.step1")}</li>
        <li>{t(device ? "accounts.guided.deviceStep2" : "accounts.guided.step2")}</li>
        <li>{t(device ? "accounts.guided.deviceStep3" : "accounts.guided.step3")}</li>
      </ol>
      {(start.error || submit.error) && <Alert tone="danger">{toMsg(start.error ?? submit.error)}</Alert>}
      {!s || s.phase === "done" || s.phase === "failed" ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => start.mutate()} loading={start.isPending}><ExternalLink className="size-4" /> {t("accounts.guided.open")}</Button>
          {s?.phase === "done" && <span className="text-sm text-accent">{t("accounts.guided.done")}</span>}
          {s?.phase === "failed" && <span className="text-sm text-danger">{t("accounts.guided.failed")}{s.message ? ` (${s.message})` : ""}</span>}
        </div>
      ) : (
        <>
          {s.phase === "starting" && <div className="flex items-center gap-2 text-sm text-fg-muted"><Spinner className="size-4" /> {t("accounts.guided.starting")}</div>}
          {s.url && (
            <div className="space-y-1">
              <div className="text-xs text-fg-muted">{t("accounts.guided.linkHint")}</div>
              <div className="flex items-center gap-2">
                <a href={s.url} target="_blank" rel="noreferrer" dir="ltr" className="min-w-0 flex-1 truncate rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-accent underline">{s.url}</a>
                <Button variant="secondary" size="sm" onClick={copy}>{copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}</Button>
                <a href={s.url} target="_blank" rel="noreferrer" className="rounded-md border border-border px-2 py-1.5 text-xs hover:bg-surface-2"><ExternalLink className="size-3.5" /></a>
              </div>
            </div>
          )}
          {device && s.userCode && (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-accent/40 bg-accent-soft/30 px-3 py-2">
              <span className="text-xs text-fg-muted">{t("accounts.guided.deviceCode")}</span>
              <code dir="ltr" className="select-all text-lg font-semibold tracking-[0.2em]">{s.userCode}</code>
              <Button variant="secondary" size="sm" onClick={() => { void copyText(s.userCode!); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}</Button>
              <span className="flex items-center gap-1 text-xs text-fg-muted"><Spinner className="size-3" /> {t("accounts.guided.deviceWaiting")}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => { void accountsApi.loginCancel(accountId); qc.setQueryData(["accounts", accountId, "login"], null); }}>{t("common.cancel")}</Button>
            </div>
          )}
          {!device && <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (code.trim()) submit.mutate(); }}>
            <Input dir="ltr" className="min-w-0 flex-1 basis-56 font-mono" autoFocus placeholder={t("accounts.guided.codePlaceholder")} value={code} onChange={(e) => setCode(e.target.value)} disabled={s.phase === "finishing"} />
            <Button type="submit" loading={submit.isPending || s.phase === "finishing"} disabled={!code.trim()}>{t("accounts.guided.confirm")}</Button>
            <Button type="button" variant="ghost" onClick={() => { void accountsApi.loginCancel(accountId); qc.setQueryData(["accounts", accountId, "login"], null); }}>{t("common.cancel")}</Button>
          </form>}
          {s.phase === "finishing" && <div className="text-xs text-fg-muted">{t("accounts.guided.finishing")}</div>}
          {s.message && s.phase === "waiting_code" && <Alert tone="danger">{s.message}</Alert>}
        </>
      )}
      {s?.output && (
        <details className="text-xs text-fg-muted">
          <summary className="cursor-pointer">{t("accounts.guided.output")}</summary>
          <pre dir="ltr" className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-surface p-2 font-mono">{s.output}</pre>
        </details>
      )}
      <button type="button" className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg" onClick={() => setAdvanced(true)}><TerminalSquare className="size-3.5" /> {t("accounts.guided.advanced")}</button>
    </div>
  );
}
